import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  PenLine,
  Repeat2,
  ImageIcon,
  Bookmark,
  BotMessageSquare,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  type LucideIcon,
} from "lucide-react";
import { MARKETING_SITE_URL } from "@/lib/constants";

export type StudioSection =
  | "rewrite"
  | "repurpose"
  | "images"
  | "workspace"
  | "ai-coach";

interface StudioSidebarProps {
  section: StudioSection;
  onSelectSection: (section: StudioSection) => void;
  collapsed: boolean;
  onCollapsedChange: (next: boolean) => void;
}

interface NavItem {
  id: StudioSection;
  label: string;
  icon: LucideIcon;
}

const NAV_ITEMS: NavItem[] = [
  { id: "rewrite", label: "Rewrite", icon: PenLine },
  { id: "repurpose", label: "Repurpose", icon: Repeat2 },
  { id: "images", label: "Images", icon: ImageIcon },
  { id: "workspace", label: "Workspace", icon: Bookmark },
  { id: "ai-coach", label: "AI Coach", icon: BotMessageSquare },
];

const COLLAPSED_W = 56;
const EXPANDED_W = 240;
const StudioSidebar = ({
  section,
  onSelectSection,
  collapsed,
  onCollapsedChange,
}: StudioSidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = (id: StudioSection) => {
    if (location.pathname !== "/") {
      navigate("/");
    }
    onSelectSection(id);
  };

  return (
    <aside
      aria-label="Studio sections"
      className="hidden lg:flex shrink-0 flex-col border-r"
      style={{
        width: collapsed ? COLLAPSED_W : EXPANDED_W,
        background: "#FFFFFF",
        borderRightColor: "rgba(15,23,42,0.08)",
        transition: "width 180ms ease",
      }}
    >
      <div
        className="flex items-center px-2"
        style={{ height: 56, justifyContent: collapsed ? "center" : "space-between" }}
      >
        {!collapsed && (
          <a
            href={MARKETING_SITE_URL}
            className="flex min-w-0 items-center gap-2"
            aria-label="Vinci Writer — back to homepage"
          >
            <div
              aria-hidden
              className="flex items-center justify-center shrink-0"
              style={{
                width: 30,
                height: 30,
                borderRadius: 7,
                background: "linear-gradient(135deg, #6366F1 0%, #3B82F6 100%)",
              }}
            >
              <span
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 900,
                  fontSize: 16,
                  color: "#fff",
                  lineHeight: 1,
                }}
              >
                V
              </span>
            </div>
            <span
              className="truncate"
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 700,
                fontSize: 15,
                color: "#0f172a",
                lineHeight: 1,
              }}
            >
              Vinci Writer
            </span>
          </a>
        )}
        <button
          type="button"
          onClick={() => onCollapsedChange(!collapsed)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100"
        >
          {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-2">
        <ul className="flex flex-col gap-1">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = section === item.id;
            const iconColor = active ? "#6366F1" : "#64748B";
            return (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => handleClick(item.id)}
                  aria-current={active ? "page" : undefined}
                  title={collapsed ? item.label : undefined}
                  className="flex w-full items-center rounded-lg transition-colors"
                  style={{
                    minHeight: 44,
                    padding: collapsed ? 0 : "0 10px",
                    justifyContent: collapsed ? "center" : "flex-start",
                    gap: 10,
                    background: active ? "#EEF2FF" : "transparent",
                    color: active ? "#4F46E5" : "#334155",
                    fontWeight: active ? 600 : 500,
                    fontSize: 13,
                  }}
                >
                  <Icon
                    size={18}
                    strokeWidth={active ? 2.2 : 1.8}
                    style={{ color: iconColor, flexShrink: 0 }}
                    aria-hidden
                  />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      <div
        className="border-t px-2 py-2"
        style={{ borderTopColor: "rgba(15,23,42,0.08)" }}
      >
        <Link
          to="/settings"
          className="flex w-full items-center rounded-lg text-slate-600 transition-colors hover:bg-slate-100"
          style={{
            minHeight: 44,
            padding: collapsed ? 0 : "0 10px",
            justifyContent: collapsed ? "center" : "flex-start",
            gap: 10,
            fontSize: 13,
            fontWeight: 500,
            textDecoration: "none",
          }}
          title={collapsed ? "Settings" : undefined}
        >
          <Settings size={18} strokeWidth={1.8} aria-hidden />
          {!collapsed && <span className="truncate">Settings</span>}
        </Link>
      </div>
    </aside>
  );
};

const SIDEBAR_STORAGE_KEY = "vml_sidebar_collapsed";

export function useSidebarCollapsedState(): [boolean, (next: boolean) => void] {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, collapsed ? "1" : "0");
    } catch {
      /* quota exceeded — silent */
    }
  }, [collapsed]);
  return [collapsed, setCollapsed];
}

export default StudioSidebar;
